import type {
  Season,
  SeasonCalendar,
  SeasonCalendarDay,
  SeasonPayload,
  SeasonRentalAssignment,
} from '@interfaces/seasons.interface';
import { findEqualPriorityConflicts } from '@pages/settings/seasons/utils/season-overlap.util';

import { mockDb } from '../mock-db';
import { BASE_PRICE, CURRENCY } from './rate-grid-mock.model';
import { manualRateGridNightsMock } from './rate-grid.functions';
import type { MockCodedRefusal } from './rental.functions';

export type SeasonMockResult = { season: Season } | { refusal: MockCodedRefusal };

export type SeasonCalendarMockResult = { calendar: SeasonCalendar } | { refusal: MockCodedRefusal };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;
const CALENDAR_MAX_DAYS = 366;

let seasonCounter = 0;

/**
 * A fresh season id. The counter, not the clock, is what makes it unique: two creates fired in the
 * same millisecond would otherwise share `season-${Date.now()}` and the second would overwrite the
 * first everywhere the list is keyed by id.
 */
function nextSeasonId(): string {
  seasonCounter += 1;

  return `season-${Date.now()}-${seasonCounter}`;
}

function refusal(status: number, code: string, details: Record<string, unknown> = {}) {
  return { refusal: { status, code, details } };
}

/** The body as the server stores it: name trimmed and colour lower-cased. */
function normalised(body: SeasonPayload): SeasonPayload {
  return { ...body, name: body.name.trim(), colour: body.colour.toLowerCase() };
}

/** The created season per `Idempotency-Key`, so a retried create replays instead of adding a copy. */
const createdByKey = new Map<string, Season>();

/** The version an `If-Match` names: a quoted entity tag (`"3"`) or, as the backend also accepts, a bare one. */
function versionOf(ifMatch: string): string {
  const value = ifMatch.trim();
  return /^"\d+"$/.test(value) ? value.slice(1, -1) : value;
}

/**
 * Checks a write against the season it targets, as the backend does: an unknown id is
 * `SEASON_NOT_FOUND` and an `If-Match` other than the stored version is `SEASON_VERSION_CONFLICT`;
 * an absent `If-Match` is not checked.
 */
function guardWrite(seasonId: string, ifMatch: string | null): Season | MockCodedRefusal {
  const current = mockDb.getSeasonById(seasonId);
  if (!current) {
    return { status: 404, code: 'SEASON_NOT_FOUND', details: {} };
  }
  const expected = ifMatch === null ? null : versionOf(ifMatch);
  if (expected !== null && expected !== String(current.version)) {
    return {
      status: 409,
      code: 'SEASON_VERSION_CONFLICT',
      details: { currentVersion: current.version, expectedVersion: Number(expected) },
    };
  }
  return current;
}

/** `SEASON_OVERLAP` with the colliding seasons in `details.conflicts`, or `null` when there is none. */
function overlapRefusal(id: string | null, body: SeasonPayload): SeasonMockResult | null {
  const conflicts = findEqualPriorityConflicts({ id, ...body }, mockDb.getSeasons());
  return conflicts.length ? refusal(409, 'SEASON_OVERLAP', { conflicts }) : null;
}

/**
 * Returns the host's full season list, as `GET /settings/seasons` would.
 */
export function getSeasonsMock(): Season[] {
  return mockDb.getSeasons();
}

/**
 * Creates a season at version 1, as `POST /settings/seasons` would, refusing an equal-priority
 * overlap with `SEASON_OVERLAP`. A create retried under the `Idempotency-Key` of one that already
 * landed returns that season instead of adding a second one.
 */
export function createSeasonMock(
  body: SeasonPayload,
  idempotencyKey: string | null = null
): SeasonMockResult {
  const replayed = idempotencyKey === null ? undefined : createdByKey.get(idempotencyKey);
  if (replayed) {
    return { season: replayed };
  }
  const payload = normalised(body);
  const refused = overlapRefusal(null, payload);
  if (refused) {
    return refused;
  }
  const season = mockDb.addSeason({ id: nextSeasonId(), ...payload, version: 1 });
  if (idempotencyKey !== null) {
    createdByKey.set(idempotencyKey, season);
  }
  return { season };
}

/**
 * Replaces a season's fields and bumps its version, as `PATCH /settings/seasons/{seasonId}` would,
 * after the `If-Match` and overlap checks.
 */
export function updateSeasonMock(
  seasonId: string,
  body: SeasonPayload,
  ifMatch: string | null
): SeasonMockResult {
  const current = guardWrite(seasonId, ifMatch);
  if ('code' in current) {
    return { refusal: current };
  }
  const payload = normalised(body);
  const updated: Season = { id: seasonId, ...payload, version: current.version + 1 };
  return (
    overlapRefusal(seasonId, payload) ?? {
      season: mockDb.replaceSeasonById(seasonId, updated) ?? updated,
    }
  );
}

/**
 * Clones a season under a fresh id at version 1, appending "(copy)" to its name and leaving it
 * without rentals, as `POST /settings/seasons/{seasonId}/duplicate` would.
 */
export function duplicateSeasonMock(seasonId: string, ifMatch: string | null): SeasonMockResult {
  const source = guardWrite(seasonId, ifMatch);
  if ('code' in source) {
    return { refusal: source };
  }
  const duplicated: Season = {
    ...source,
    id: nextSeasonId(),
    name: `${source.name} (copy)`,
    assignment: { allRentals: false, rentalIds: [] },
    version: 1,
  };
  return { season: mockDb.addSeason(duplicated) };
}

/**
 * Removes a season, as `DELETE /settings/seasons/{seasonId}` would; the refusal when the id or the
 * `If-Match` does not hold, else `null`.
 */
export function removeSeasonMock(
  seasonId: string,
  ifMatch: string | null
): MockCodedRefusal | null {
  const current = guardWrite(seasonId, ifMatch);
  if ('code' in current) {
    return current;
  }
  mockDb.removeSeasonById(seasonId);
  return null;
}

/**
 * Replaces a season's rental assignment and bumps its version, as
 * `PUT /settings/seasons/{seasonId}/rentals` would, after the `If-Match` and overlap checks.
 */
export function assignSeasonRentalsMock(
  seasonId: string,
  assignment: SeasonRentalAssignment,
  ifMatch: string | null
): SeasonMockResult {
  const current = guardWrite(seasonId, ifMatch);
  if ('code' in current) {
    return { refusal: current };
  }
  const updated: Season = { ...current, assignment, version: current.version + 1 };
  return (
    overlapRefusal(seasonId, updated) ?? {
      season: mockDb.replaceSeasonById(seasonId, updated) ?? updated,
    }
  );
}

/** The season that wins `date` on `rentalId`: the lowest priority among those covering it. */
function winningSeason(rentalId: string, date: string): Season | null {
  const covering = mockDb
    .getSeasons()
    .filter(
      season =>
        (season.assignment.allRentals || season.assignment.rentalIds.includes(rentalId)) &&
        season.ranges.some(range => date >= range.from && date <= range.to)
    );
  return covering.reduce<Season | null>(
    (best, season) => (best === null || season.priority < best.priority ? season : best),
    null
  );
}

function seasonPrice(season: Season): number {
  return season.rule.type === 'FIXED'
    ? season.rule.value
    : Math.round(BASE_PRICE * season.rule.value) / 100;
}

/**
 * `GET /rentals/{rentalId}/seasons/calendar?from&to`, resolved night by night from the mock season
 * catalogue over the rate grid mock's base price. A night the rate grid mock holds with a manual
 * price keeps it as `MANUAL`. `SEASON_INVALID` is 400 for a missing or malformed date and 422 for a
 * reversed or longer than 366-day window, as the backend answers.
 */
export function getSeasonCalendarMock(
  rentalId: string,
  from: string | null,
  to: string | null
): SeasonCalendarMockResult {
  if (!from || !to || !ISO_DATE.test(from) || !ISO_DATE.test(to)) {
    return refusal(400, 'SEASON_INVALID');
  }
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return refusal(400, 'SEASON_INVALID');
  }
  if (end < start || (end - start) / DAY_MS + 1 > CALENDAR_MAX_DAYS) {
    return refusal(422, 'SEASON_INVALID');
  }

  const manual = manualRateGridNightsMock(rentalId, from, to);
  const days: SeasonCalendarDay[] = [];
  for (let time = start; time <= end; time += DAY_MS) {
    const date = new Date(time).toISOString().slice(0, 10);
    const season = winningSeason(rentalId, date);
    const manualPrice = manual.get(date);
    days.push({
      date,
      seasonId: season?.id ?? null,
      seasonName: season?.name ?? null,
      colour: season?.colour ?? null,
      effective: {
        price: manual.has(date) ? (manualPrice ?? null) : season ? seasonPrice(season) : BASE_PRICE,
        minStay: null,
        maxStay: null,
        extraGuestPrice: null,
      },
      priceSource: manual.has(date) ? 'MANUAL' : season ? 'SEASON' : 'RENTAL_BASE',
    });
  }

  return {
    calendar: {
      rentalId,
      currency: CURRENCY,
      from,
      to,
      status: 'SEASON_PREVIEW_NOT_PUBLISHED',
      days,
    },
  };
}
